import { orchestrator } from 'satcheljs';
import { refreshUpNextCalendarEventAction } from '../actions/internalActions';
import { getScenarioActions } from '../actions/getScenarioActions';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { now, DisplayDate, startOfDay, endOfDay, addMilliseconds } from 'owa-datetime';
import {
    getAgendaLockedCalendarEventsReader,
    getLockedCalendarEventsStore,
} from '../selectors/calendarEventsLockedStoreSelectors';
import { getScenarioSelectors } from '../selectors/agendaStoreSelectors';
import { isBefore, differenceInMinutes } from 'owa-datetime';

orchestrator(refreshUpNextCalendarEventAction, async actionMessage => {
    refreshUpNextCalendarEvent(actionMessage.folderIds, actionMessage.scenarioId);
});

export async function refreshUpNextCalendarEvent(
    folderIds: string[],
    scenarioId: string
): Promise<null> {
    const {
        isUpNextCalendarEvent,
        upNextCalendarEventIndex,
        getEventLockCacheId,
        getPane,
    } = getScenarioSelectors(scenarioId);
    const { setUpNextCalendarEventInStore } = getScenarioActions(scenarioId);
    const lockId = getEventLockCacheId();
    const { fetchFullCalendarEvent } = getLockedCalendarEventsStore(lockId);
    const { getOrderedEvents } = getAgendaLockedCalendarEventsReader(lockId);

    const currentTime = now();
    const start = addMilliseconds(startOfDay(currentTime), 1);
    const todayDateRange = { start: start, end: endOfDay(currentTime) };
    const events = getOrderedEvents(folderIds, todayDateRange);
    const upNextEvents = events.filter(isUpNext(currentTime));
    const upNextEvent = upNextEvents.length > 0 ? upNextEvents[0] : null;

    if (upNextEvent && upNextEvent.ItemId.Id !== getPane().nextCalendarEvent.id) {
        // if the up next has changed, fetch full calendar event and store it in the events cache
        fetchFullCalendarEvent(upNextEvent.ItemId, upNextEvent.ParentFolderId.Id);
    }

    const upNextEventActionMessage = upNextEvent
        ? { id: upNextEvent.ItemId.Id, index: events.indexOf(upNextEvent) }
        : { id: null, index: null };
    if (
        !isUpNextCalendarEvent(upNextEventActionMessage.id) ||
        upNextCalendarEventIndex() !== upNextEventActionMessage.index
    ) {
        /** set the up next calendar in the store only if it has changed
         * to avoid unecessary component rerenders */
        setUpNextCalendarEventInStore(upNextEventActionMessage);
    }
    return null;
}

/** Returns true if the event is in the future */
function isUpNext(currentTime: DisplayDate): (event: CalendarEvent) => boolean {
    return (event: CalendarEvent): boolean => {
        const eventIsInThePast = isBefore(event.End, currentTime);
        const eventStartedMoreThanFiveMinsAgo = differenceInMinutes(currentTime, event.Start) > 5;
        const eventExistsOnServer =
            event.ItemId.Id !==
            event.ClientSeriesId; /** if true, this calendar item has not been created on the server, so it cannot be the upnext event */
        return (
            !eventIsInThePast &&
            !event.IsAllDayEvent &&
            !event.IsCancelled &&
            !eventStartedMoreThanFiveMinsAgo &&
            eventExistsOnServer
        );
    };
}
